// PlaceAutocomplete.jsx — birth-place input with a live suggestions dropdown.
//
// The user types, suggestions load from the geocoder (PlaceSearchService), and
// picking one calls onSelect(place) so the caller can store the label AND the
// coordinates/timezone (used directly by the Astro chart, no second guess-y
// geocode). Typing again after a pick clears the confirmed location via
// onTextChange, so the label and coordinates can never disagree.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchBirthPlaces } from './PlaceSearchService';

export default function PlaceAutocomplete({ value, geo, onTextChange, onSelect, style, placeholder = 'Type city, then pick from the list' }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState(null);
  const [searched, setSearched] = useState(false);
  const [fallbackQuery, setFallbackQuery] = useState('');
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const close = useCallback(() => { setOpen(false); setActive(-1); }, []);

  const measure = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  };

  const runSearch = (text) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    const q = (text || '').trim();
    if (q.length < 2) { setOptions([]); setLoading(false); setSearched(false); setError(''); setOpen(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const { results, usedQuery } = await searchBirthPlaces(q, ctrl.signal);
        setOptions(results);
        setFallbackQuery(usedQuery !== q ? usedQuery : '');
        setError('');
        setSearched(true);
        setActive(results.length ? 0 : -1);
        measure();
        setOpen(true);
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        setOptions([]);
        setError('Could not load places — check your internet connection.');
        setSearched(true);
        measure();
        setOpen(true);
      } finally {
        if (abortRef.current === ctrl) setLoading(false);
      }
    }, 350);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // A fixed-position dropdown would drift away from its input when the page or
  // the modal scrolls — close it instead (scrolling inside the list is fine).
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => { if (listRef.current && listRef.current.contains(e.target)) return; close(); };
    const onDown = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close]);

  const pick = (place) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    onSelect(place);
    setOptions([]);
    setLoading(false);
    close();
  };

  const onKeyDown = (e) => {
    if (!open || !options.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % options.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + options.length) % options.length); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(options[active]); }
    else if (e.key === 'Escape') { close(); }
  };

  const hasText = !!(value && value.trim());
  const status = geo ? 'ok' : (hasText ? 'warn' : null);
  const dropWidth = rect ? Math.max(rect.width, 260) : 260;
  const dropLeft = rect ? Math.min(rect.left, Math.max(8, window.innerWidth - dropWidth - 8)) : 0;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        style={{ ...style, paddingRight: '28px' }}
        placeholder={placeholder}
        value={value || ''}
        autoComplete="off"
        onChange={(e) => { onTextChange(e.target.value); runSearch(e.target.value); }}
        onKeyDown={onKeyDown}
        onFocus={(e) => { e.currentTarget.style.borderColor = '#00A19D'; if (options.length) { measure(); setOpen(true); } }}
        onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
      />
      <span
        title={geo ? `Location confirmed (${Number(geo.latitude).toFixed(3)}, ${Number(geo.longitude).toFixed(3)})` : (hasText ? 'Pick a place from the suggestions for an accurate chart' : '')}
        style={{ position: 'absolute', right: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', fontWeight: 700, lineHeight: 1, pointerEvents: 'auto', cursor: 'default',
          color: loading ? '#94a3b8' : status === 'ok' ? '#16a34a' : '#d97706' }}
      >
        {loading ? '…' : status === 'ok' ? '✓' : status === 'warn' ? '!' : ''}
      </span>

      {open && rect && (
        <div
          ref={listRef}
          style={{
            position: 'fixed', top: rect.bottom + 4, left: dropLeft, width: dropWidth, maxHeight: '260px', overflowY: 'auto',
            background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', boxShadow: '0 10px 28px rgba(15,23,42,0.22)', zIndex: 20000,
          }}
        >
          {error && <div style={{ padding: '10px 12px', fontSize: '12px', color: '#b91c1c' }}>{error}</div>}
          {!error && fallbackQuery && options.length > 0 && (
            <div style={{ padding: '6px 12px', fontSize: '11px', color: '#92400e', background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
              No exact match — showing places starting with “{fallbackQuery}”.
            </div>
          )}
          {!error && searched && options.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: '#64748b' }}>
              No matching place — check the spelling or type fewer letters (e.g. "faiz" instead of the full name).
            </div>
          )}
          {options.map((o, i) => (
            <div
              key={o.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
              onMouseEnter={() => setActive(i)}
              style={{ padding: '8px 12px', cursor: 'pointer', background: i === active ? '#e6f7f6' : '#fff', borderBottom: i < options.length - 1 ? '1px solid #f1f5f9' : 'none' }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{o.label}</div>
              {o.detail && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{o.detail}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
